// HUB-UI: public job application form. CVs upload through the authenticated self endpoint.
import { useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { useAppSelector } from '../../app/store';
import { http } from '../../core/http/client';
import { write } from './hubApi';
import { errorMessage } from './HubUi';

export function ApplyDialog({ jobId, close }: { jobId: string; close: () => void }) {
  const profile = useAppSelector((state) => state.session.profile);
  const [phone, setPhone] = useState(profile.phone ?? '');
  const [coverLetter, setCoverLetter] = useState('');
  const [availableFrom, setAvailableFrom] = useState('');
  const [experienceYears, setExperienceYears] = useState('');
  const [cv, setCv] = useState<File | null>(null);
  const [cvFileId, setCvFileId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={busy ? undefined : close} aria-labelledby="hub-apply-title">
      <Box
        component="form"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!cv && !cvFileId) {
            setError('Choose a PDF CV.');
            return;
          }
          if (cv && (cv.type !== 'application/pdf' || cv.size > 10 * 1024 * 1024)) {
            setError('Choose a PDF smaller than 10 MB.');
            return;
          }
          setBusy(true);
          setError(null);
          try {
            let fileId = cvFileId;
            if (!fileId && cv) {
              const body = new FormData();
              body.append('file', cv);
              body.append('kind', 'Cv');
              const result = await http().post<{ fileId: string }>('/me/files', body, { skipErrorToast: true });
              fileId = result.data.fileId;
              if (!fileId) throw new Error('The CV upload did not return a file ID.');
              setCvFileId(fileId);
            }
            await write(
              '/me/applications',
              {
                jobId,
                phone,
                coverLetter: coverLetter || null,
                availableFrom: availableFrom || null,
                experienceYears: experienceYears === '' ? null : Number(experienceYears),
                cvFileId: fileId,
              },
              'post',
            );
            close();
          } catch (caught) {
            setError(errorMessage(caught));
          } finally {
            setBusy(false);
          }
        }}
      >
        <DialogTitle id="hub-apply-title">Apply for this position</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Phone"
              required
              value={phone}
              disabled={busy}
              onChange={(event) => setPhone(event.target.value)}
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              label="Cover letter"
              multiline
              minRows={4}
              value={coverLetter}
              disabled={busy}
              onChange={(event) => setCoverLetter(event.target.value)}
              inputProps={{ maxLength: 4000 }}
            />
            <TextField
              label="Available from"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={availableFrom}
              disabled={busy}
              onChange={(event) => setAvailableFrom(event.target.value)}
            />
            <TextField
              label="Years of experience"
              type="number"
              value={experienceYears}
              inputProps={{ min: 0, max: 60, step: 1 }}
              disabled={busy}
              onChange={(event) => setExperienceYears(event.target.value)}
            />
            <Button component="label" disabled={busy}>
              Choose PDF CV
              <input
                type="file"
                hidden
                accept="application/pdf"
                onChange={(event) => {
                  setCv(event.target.files?.[0] ?? null);
                  setCvFileId(null);
                }}
              />
            </Button>
            <span>{cv?.name}</span>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" disabled={busy}>
            {busy ? 'Submitting…' : 'Submit application'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
